import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container } from '@/components/container';
import { DataGrid, KeenIcon } from '@/components';
import usePagination from '@/hooks/usePagination';
import { useSnackbar } from 'notistack';
import {
  useGetTransactionsListQuery,
  useGetTransactionsSummaryQuery,
  useLazyExportTransactionsCsvQuery,
} from '@/services/TransactionsService';
import {
  TransactionItem,
  TransactionStatus,
  TransactionType,
  TransactionsListParams,
} from '@/types/transactions.types';
import DateRangePicker from '@/components/inputControls/DateRangePicker';
import moment from 'moment';
import { formatFinnishCurrency, formatFinnishNumber } from '@/utils';
import { formatDisplayDateTime } from '@/utils/Date';

const numberFormat = (n: number) => new Intl.NumberFormat().format(n);

const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  SUBSCRIPTION: 'Subscription',
  CLUB_RESOURCE: 'Club Resource',
  PORT_WATCH_BUYOUT: 'Port Watch Buy-Out',
  BOOKING: 'Booking',
  ADDON: 'Add-on',
  SERVICE_BOOKING: 'Service Booking',
};

const TRANSACTION_STATUS_LABELS: Record<string, string> = {
  SUCCEEDED: 'Succeeded',
  FAILED: 'Failed',
  REFUNDED: 'Refunded',
  PENDING: 'Pending',
};

const humanizeType = (v: string) =>
  (v || '')
    .toString()
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const Transactions = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  // Date range in DD/MM/YYYY for UI component
  const [dateRange, setDateRange] = useState<
    [string | undefined, string | undefined]
  >([undefined, undefined]);
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();

  const apiStart = dateRange[0]
    ? moment(dateRange[0], 'DD/MM/YYYY').format('YYYY-MM-DD')
    : undefined;
  const apiEnd = dateRange[1]
    ? moment(dateRange[1], 'DD/MM/YYYY').format('YYYY-MM-DD')
    : undefined;

  const listParams: TransactionsListParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue,
    start_date: apiStart,
    end_date: apiEnd,
    type: typeFilter !== 'all' ? (typeFilter as TransactionType) : undefined,
    status:
      statusFilter !== 'all' ? (statusFilter as TransactionStatus) : undefined,
  };

  const {
    data: summaryData,
    isFetching: summaryLoading,
    error: summaryError,
  } = useGetTransactionsSummaryQuery();

  const {
    data: list,
    isFetching: listLoading,
    error: listError,
  } = useGetTransactionsListQuery(listParams);

  const exportQueryParams = useMemo(
    () => ({
      search: debouncedValue,
      start_date: apiStart,
      end_date: apiEnd,
      type: typeFilter !== 'all' ? (typeFilter as TransactionType) : undefined,
      status:
        statusFilter !== 'all'
          ? (statusFilter as TransactionStatus)
          : undefined,
    }),
    [debouncedValue, apiStart, apiEnd, typeFilter, statusFilter]
  );

  // Lazy export hook (blob download)
  const [exportCsv, { isLoading: isExportLoading }] =
    useLazyExportTransactionsCsvQuery();

  // Reset to first page when filters change
  useEffect(() => {
    setPagination((p) => ({ ...p, pageIndex: 0 }));
  }, [dateRange, typeFilter, statusFilter, setPagination]);

  const rows: TransactionItem[] = useMemo(() => list?.items ?? [], [list]);

  useEffect(() => {
    if (list) {
      handleTotalPages(list.totalPages || 1);
      handleTotalItems(list.totalItems || 0);
    }
  }, [list, handleTotalItems, handleTotalPages]);

  useEffect(() => {
    const err = (listError || summaryError) as unknown as {
      data?: unknown;
      error?: string;
    } | null;
    if (!err) return;
    let message = 'Failed to fetch transactions';
    if (
      err?.data &&
      typeof err.data === 'object' &&
      'message' in (err.data as Record<string, unknown>)
    ) {
      const m = (err.data as { message?: string }).message;
      if (typeof m === 'string' && m) message = m;
    } else if (typeof err?.error === 'string' && err.error) {
      message = err.error;
    }
    enqueueSnackbar(message, { variant: 'error' });
  }, [listError, summaryError, enqueueSnackbar]);

  const columns = [
    {
      accessorFn: (row: TransactionItem) => row.type,
      id: 'type',
      header: 'Type',
      enableSorting: false,
      cell: ({ row }: { row: { original: TransactionItem } }) => (
        <span>
          {TRANSACTION_TYPE_LABELS[row.original.type] ||
            humanizeType(row.original.type)}
        </span>
      ),
    },
    {
      accessorFn: (row: TransactionItem) => row.status,
      id: 'status',
      header: 'Status',
      enableSorting: false,
      cell: ({ row }: { row: { original: TransactionItem } }) => (
        <span
          className={`badge ${row.original.status === 'SUCCEEDED' ? 'badge-success' : row.original.status === 'FAILED' ? 'badge-danger' : 'badge-warning'}`}
        >
          {row.original.status}
        </span>
      ),
    },
    {
      accessorFn: (row: TransactionItem) => row.amount,
      id: 'amount',
      header: 'Amount',
      enableSorting: false,
      meta: {
        className: 'text-end',
        cellClassName: 'text-end',
      },
      cell: ({ row }: { row: { original: TransactionItem } }) => (
        <span>
          {formatFinnishCurrency(row.original.amount, {
            currency: row.original.currency,
          })}
        </span>
      ),
    },
    {
      accessorFn: (row: TransactionItem) => row.refunded_amount,
      id: 'refunded',
      header: 'Refunded',
      enableSorting: false,
      meta: {
        className: 'text-end',
        cellClassName: 'text-end',
      },
      cell: ({ row }: { row: { original: TransactionItem } }) => (
        <span>
          {formatFinnishCurrency(row.original.refunded_amount, {
            currency: row.original.currency,
          })}
        </span>
      ),
    },
    {
      accessorFn: (row: TransactionItem) => row.currency,
      id: 'currency',
      header: 'Currency',
      enableSorting: false,
      cell: ({ row }: { row: { original: TransactionItem } }) => (
        <span>{(row.original.currency || '').toUpperCase()}</span>
      ),
    },
    {
      accessorFn: (row: TransactionItem) =>
        row.payment_intent_id || row.paytrail_payment_id || '',
      id: 'psp_transaction_id',
      header: 'PSP Transaction ID',
      enableSorting: false,
      cell: ({ row }: { row: { original: TransactionItem } }) => {
        const t = row.original;
        return (
          <div className="flex flex-col">
            {t.payment_intent_id && (
              <span className="text-xs">
                Stripe PaymentIntent ID: {t.payment_intent_id}
              </span>
            )}
            {t.paytrail_payment_id && (
              <span className="text-xs">
                Paytrail Transaction ID: {t.paytrail_payment_id}
              </span>
            )}
            {t.stripe_refund_id && (
              <span className="text-xs">Refund: {t.stripe_refund_id}</span>
            )}
            {!t.payment_intent_id &&
              !t.paytrail_payment_id &&
              !t.stripe_refund_id && (
                <span className="text-xs text-gray-500">—</span>
              )}
          </div>
        );
      },
    },
    {
      accessorFn: (row: TransactionItem) => row.created_at,
      id: 'created_at',
      header: 'Created At',
      enableSorting: false,
      cell: ({ row }: { row: { original: TransactionItem } }) => (
        <span className="text-sm">
          {formatDisplayDateTime(new Date(row.original.created_at))}
        </span>
      ),
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }: { row: { original: TransactionItem } }) => (
        <button
          onClick={() => navigate(`/transactions/${row.original.id}`)}
          className="btn btn-sm btn-icon btn-light btn-outline"
          title="View transaction details"
        >
          <KeenIcon icon="eye" className="text-lg" />
        </button>
      ),
    },
  ];

  const s = summaryData;

  return (
    <Container>
      <div className="flex items-center justify-between mb-6">
        <h1
          id="transactions-heading"
          className="text-xl font-semibold"
          tabIndex={-1}
        >
          Transactions
        </h1>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <div className="card shadow-sm">
          <div className="card-body p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm text-gray-500">Total Transactions</p>
                <p className="text-2xl font-semibold">
                  {summaryLoading
                    ? '--'
                    : formatFinnishNumber(s?.totalTransactions ?? 0)}
                </p>
              </div>
              <KeenIcon icon="credit-cart" className="text-2xl text-primary" />
            </div>
          </div>
        </div>
        <div className="card shadow-sm">
          <div className="card-body p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm text-gray-500">Total Successful Amount</p>
                <p className="text-2xl font-semibold">
                  {summaryLoading
                    ? '--'
                    : formatFinnishCurrency(s?.totalSuccessfulAmount ?? 0)}
                </p>
              </div>
              <KeenIcon icon="chart-line" className="text-2xl text-primary" />
            </div>
          </div>
        </div>
        <div className="card shadow-sm">
          <div className="card-body p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm text-gray-500">Failed Transactions</p>
                <p className="text-2xl font-semibold">
                  {summaryLoading
                    ? '--'
                    : numberFormat(s?.failedTransactions ?? 0)}
                </p>
              </div>
              <KeenIcon icon="cross-circle" className="text-2xl text-danger" />
            </div>
          </div>
        </div>
        <div className="card shadow-sm">
          <div className="card-body p-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm text-gray-500">Top Types</p>
                <p className="text-base font-semibold text-gray-800">
                  {summaryLoading
                    ? '--'
                    : (s?.transactionsByType || [])
                        .slice(0, 3)
                        .map(
                          (t) =>
                            `${TRANSACTION_TYPE_LABELS[t.type] || humanizeType(String(t.type))} (${t.count})`
                        )
                        .join(', ') || '—'}
                </p>
              </div>
              <KeenIcon icon="category" className="text-2xl text-primary" />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="transactions-listings card">
        <div className="card-body">
          <div className="card-header px-0 pt-0 pb-4 flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              {/* Search first */}
              <div className="flex-shrink-0">
                <label
                  className="input input-sm h-8 sm:h-10 flex items-center"
                  htmlFor="search-transactions"
                  aria-label="Search by transaction or order ID"
                >
                  <KeenIcon
                    type="filled"
                    icon="magnifier"
                    className="text-gray-500"
                  />
                  <input
                    id="search-transactions"
                    placeholder="Search by transaction or order ID"
                    type="text"
                    value={searchTerm}
                    onChange={handleSearch}
                    className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                    aria-label="Search by transaction or order ID"
                  />
                </label>
              </div>
              {/* Type filter */}
              <div className="flex-shrink-0">
                <select
                  className="select select-sm w-28 sm:w-32 h-8 sm:h-10 text-sm"
                  style={{ minWidth: 'max-content' }}
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  aria-label="Filter by transaction type"
                >
                  <option value="all">All Types</option>
                  <option value={TransactionType.BOOKING}>
                    {TRANSACTION_TYPE_LABELS[TransactionType.BOOKING]}
                  </option>
                  <option value={TransactionType.ADDON}>
                    {TRANSACTION_TYPE_LABELS[TransactionType.ADDON]}
                  </option>
                  <option value={TransactionType.SUBSCRIPTION}>
                    {TRANSACTION_TYPE_LABELS[TransactionType.SUBSCRIPTION]}
                  </option>
                  <option value={TransactionType.CLUB_RESOURCE}>
                    {TRANSACTION_TYPE_LABELS[TransactionType.CLUB_RESOURCE]}
                  </option>
                  <option value={TransactionType.SERVICE_BOOKING}>
                    {TRANSACTION_TYPE_LABELS[TransactionType.SERVICE_BOOKING]}
                  </option>
                </select>
              </div>
              {/* Status filter */}
              <div className="flex-shrink-0">
                <select
                  className="select select-sm w-28 sm:w-32 h-8 sm:h-10 text-sm"
                  style={{ minWidth: 'max-content' }}
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  aria-label="Filter by transaction status"
                >
                  <option value="all">All Status</option>
                  <option value={TransactionStatus.SUCCEEDED}>
                    {TRANSACTION_STATUS_LABELS[TransactionStatus.SUCCEEDED]}
                  </option>
                  <option value={TransactionStatus.FAILED}>
                    {TRANSACTION_STATUS_LABELS[TransactionStatus.FAILED]}
                  </option>
                  <option value={TransactionStatus.REFUNDED}>
                    {TRANSACTION_STATUS_LABELS[TransactionStatus.REFUNDED]}
                  </option>
                  <option value={TransactionStatus.PENDING}>
                    {TRANSACTION_STATUS_LABELS[TransactionStatus.PENDING]}
                  </option>
                </select>
              </div>
              {/* Date Range */}
              <div className="flex-shrink-0">
                <DateRangePicker
                  startDateName="start_date"
                  endDateName="end_date"
                  isMandatory={false}
                  label={undefined}
                  value={dateRange}
                  onChange={(dates) => setDateRange(dates)}
                  allowPastDates={true}
                  allowFutureDates={false}
                  className="w-full sm:w-56 md:w-64 h-8 sm:h-10 text-sm"
                />
              </div>
            </div>
            {/* Actions at end: Export CSV (always) + Clear Filters (conditional) */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                className="btn btn-sm btn-primary flex items-center gap-2 h-8 sm:h-10 text-white"
                onClick={async () => {
                  try {
                    const blob = await exportCsv(
                      exportQueryParams as Omit<
                        TransactionsListParams,
                        'page' | 'limit'
                      >
                    ).unwrap();
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    const date = new Date().toISOString().slice(0, 10);
                    link.setAttribute('download', `transactions_${date}.csv`);
                    document.body.appendChild(link);
                    link.click();
                    link.parentNode?.removeChild(link);
                    window.URL.revokeObjectURL(url);
                    enqueueSnackbar('CSV exported successfully', {
                      variant: 'success',
                    });
                  } catch (e) {
                    let message = 'Failed to export transactions';
                    if (
                      e &&
                      typeof e === 'object' &&
                      'data' in (e as Record<string, unknown>)
                    ) {
                      const data = (e as { data?: { message?: string } }).data;
                      if (data?.message) message = data.message;
                    } else if (
                      e &&
                      typeof e === 'object' &&
                      'error' in (e as Record<string, unknown>)
                    ) {
                      const errStr = (e as { error?: string }).error;
                      if (errStr) message = errStr;
                    }
                    enqueueSnackbar(message, { variant: 'error' });
                  }
                }}
                aria-label={
                  isExportLoading
                    ? 'Exporting... transactions as CSV'
                    : 'Export CSV transactions'
                }
                title="Export CSV"
                disabled={isExportLoading}
              >
                {/* <KeenIcon type="solid" icon="export" className="text-white" /> */}
                {isExportLoading ? 'Exporting...' : 'Export CSV'}
              </button>
              {(!!searchTerm ||
                !!dateRange[0] ||
                !!dateRange[1] ||
                typeFilter !== 'all' ||
                statusFilter !== 'all') && (
                <button
                  className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10"
                  onClick={() => {
                    if (searchTerm) resetSearch();
                    setDateRange([undefined, undefined]);
                    setTypeFilter('all');
                    setStatusFilter('all');
                    setPagination((p) => ({ ...p, pageIndex: 0 }));
                  }}
                  aria-label="Clear filters"
                  type="button"
                >
                  <KeenIcon
                    type="solid"
                    icon="arrows-circle"
                    className="text-gray-600"
                  />
                  Clear Filters
                </button>
              )}
            </div>
          </div>
          <DataGrid
            columns={columns}
            data={rows}
            rowSelect={false}
            loading={listLoading}
            pagination={{
              page: pagination.pageIndex,
              size: pagination.pageSize,
              more: true,
              totalItems,
              totalPages,
              setPagination,
              info: '{from} - {to} of {count}',
              sizes: [5, 10, 25, 50, 100],
              sizesLabel: 'Show',
              sizesDescription: 'per page',
            }}
            serverSide={true}
            hasActiveFilters={
              !!searchTerm ||
              !!dateRange[0] ||
              !!dateRange[1] ||
              typeFilter !== 'all' ||
              statusFilter !== 'all'
            }
            onFilterReset={() => {
              if (searchTerm) resetSearch();
              setDateRange([undefined, undefined]);
              setTypeFilter('all');
              setStatusFilter('all');
              setPagination((p) => ({ ...p, pageIndex: 0 }));
            }}
            aria-label="Transactions table"
          />
        </div>
      </div>
    </Container>
  );
};

export default Transactions;
